import {
  Alert,
  AlertTitle,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { sendPayInToCxP } from 'lib/api/conciliations';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { formatMoney, showAlert } from 'lib/functions/utils';
import React from 'react';
import { useDispatch } from 'react-redux';

type PayIn = {
  amount: number;
  amountConciliated: number;
  bankAccountDestinationId: number;
  bankAccountDestinationName: string;
  bankAccountOriginId: number;
  bankAccountOriginName: string;
  businessDestinationIdentifier: string;
  businessDestinationName: string;
  businessOriginIdentifier: string;
  businessOriginName: string;
  createdAt: string;
  id: number;
  status: string;
  statusTranslated: string;
};

interface SendToCxPDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  setBigOpen: (open: boolean) => void;
  payIn: PayIn;
  setUp: () => void;
}

const SendToCxPDialog = (props: SendToCxPDialogProps) => {
  const { payIn, setOpen, setBigOpen, open, setUp } = props;
  const dispatch = useDispatch();

  const { mutate: sendPayInToCxPMutation, isPending } =
    sendPayInToCxP(dispatch);

  const submitSendToCxP = async () => {
    sendPayInToCxPMutation(
      {
        payInId: payIn.id,
      },
      {
        onSuccess: () => {
          showAlert(
            dispatch,
            'success',
            'Cuentas y conciliación creadas correctamente',
          );
          void setUp();
          setBigOpen(false);
          setOpen(false);
        },
        onError: () => {
          showAlert(
            dispatch,
            'error',
            'Ha habido un error al enviar el pago a cuenta por pagar',
          );
        },
      },
    );
  };

  const payableAccountAmount = payIn.amount - payIn.amountConciliated;
  const payableAccountAmountFormatted = formatMoney(payableAccountAmount);

  return (
    <Dialog maxWidth="sm" fullWidth open={open}>
      <DialogTitle>Enviar saldo a cuenta por pagar</DialogTitle>

      {isPending ? (
        <Box
          sx={{
            minHeight: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CenteredCircularProgress type="dialog" />
        </Box>
      ) : (
        <DialogContent sx={{ p: 3 }}>
          <Alert
            severity="info"
            sx={{
              mb: 2,
              bgcolor: '#f0f7ff',
              border: '1px solid #b3d9ff',
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: '#1976d2',
              },
            }}
          >
            <AlertTitle sx={{ color: '#1976d2', fontWeight: 600 }}>
              Información importante
            </AlertTitle>
            <Typography variant="body2" sx={{ color: '#495057', mt: 1 }}>
              Se generará una cuenta por <strong>cobrar</strong> y una cuenta
              por <strong>pagar</strong>. Ambas a nombre de{' '}
              <strong>{payIn.businessOriginName}</strong> |{' '}
              <strong>{payIn.businessOriginIdentifier}</strong> por un total de{' '}
              <strong>{payableAccountAmountFormatted}</strong>.
            </Typography>
          </Alert>

          <Alert
            severity="success"
            sx={{
              bgcolor: '#f0f9f0',
              border: '1px solid #a5d6a7',
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: '#4caf50',
              },
            }}
          >
            <Typography variant="body2" sx={{ color: '#2e7d32' }}>
              · La cuenta por cobrar se pagará automáticamente.
            </Typography>
            <Typography variant="body2" sx={{ color: '#2e7d32' }}>
              · El pago recibido quedará completado.
            </Typography>
            <Typography variant="body2" sx={{ color: '#2e7d32' }}>
              · La cuenta por pagar quedará disponible para utilizarse en
              devoluciones de pasivos.
            </Typography>
          </Alert>
        </DialogContent>
      )}

      <DialogActions>
        <CustomButton
          onClick={() => setOpen(false)}
          color="secondary"
          disabled={isPending}
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={() => submitSendToCxP()}
          color="primary"
          disabled={isPending}
        >
          Enviar a CxP
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default SendToCxPDialog;
